// components/AudioControls.tsx
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Volume2, XCircle, Loader2 } from "lucide-react";
import { Theme } from "../types/theme";

type AudioState = 'idle' | 'loading' | 'playing';

interface AudioControlsProps {
  theme: Theme;
  textToSpeak?: string;
  /**
   * Called when the user clicks to start or stop audio.
   * @param action - 'start' to begin TTS, 'stop' to halt playback
   * @param callbacks - Object with `onPlayStart` (call when audio starts playing) and `onEnd` (call when audio ends)
   */
  onAudioAction?: (
    action: 'start' | 'stop',
    callbacks?: {
      onPlayStart: () => void;
      onEnd: () => void;
    }
  ) => void;
  externalState?: AudioState; // Optional controlled state
}

const AudioControls: React.FC<AudioControlsProps> = ({
  theme,
  textToSpeak,
  onAudioAction,
  externalState,
}) => {
  const [internalState, setInternalState] = useState<AudioState>('idle');
  const stateRef = useRef<AudioState>('idle');

  // Determine effective state
  const audioState = externalState || internalState;

  // Keep ref in sync
  useEffect(() => {
    stateRef.current = audioState;
  }, [audioState]);

  const handleClick = useCallback(() => {
    if (stateRef.current === 'loading') {
      // Prevent multiple API calls
      return;
    }

    if (stateRef.current === 'playing') {
      // Stop the audio
      if (!externalState) setInternalState('idle');
      onAudioAction?.('stop');
      return;
    }

    // Start loading
    if (!externalState) setInternalState('loading');
    onAudioAction?.('start', {
      onPlayStart: () => {
        if (!externalState) setInternalState('playing');
      },
      onEnd: () => {
        if (!externalState) setInternalState('idle');
      },
    });
  }, [onAudioAction]);

  const renderIcon = () => {
    switch (audioState) {
      case 'loading':
        return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'playing':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Volume2 className="w-4 h-4" />;
    }
  };

  const getTitle = () => {
    switch (audioState) {
      case 'loading':
        return 'Loading...';
      case 'playing':
        return 'Stop';
      default:
        return 'Read';
    }
  };

  if (!textToSpeak) return null;

  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={handleClick}
        disabled={audioState === 'loading'}
        className={`rounded-lg transition-all duration-200 hover:opacity-80 ${audioState === 'loading' ? 'cursor-wait opacity-70' : ''
          } ${audioState === 'playing' ? 'opacity-90' : ''}`}
        style={{
          color: theme.textSecondaryColor,
          backgroundColor: `${theme.surfaceColor}80`,
        }}
        title={getTitle()}
      >
        {renderIcon()}
      </button>
    </div>
  );
};

export default AudioControls;
